import { t_element_lookup_list, t_key, IIndexable } from 'core-ts-types';
import { IDatasource } from './datasource';
import * as _4 from 'lodash';

export interface IRelationship<V1 extends IIndexable, V2 extends IIndexable> {
  packManyToMany(d1Key: keyof V1): this;
  packOneToMany(d1Key: keyof V1): this;
  mapManyToMany(d1Key: keyof V1, iterator?: mapIterator<V1, V2>): this;
  mapOneToMany(d1Key: keyof V1, iterator?: mapIterator<V1, V2>): this;
  everyManyToMany(d1Key: keyof V1, exist: (v1: V1, v2: V2) => void): this;
  everyOneToMany(d1Key: keyof V1, exist: (v1: V1, v2: V2) => void): this;
  filterManyToMany(d1Key: keyof V1, iterator?: (v1: V1, v2: V2) => boolean): { v1: V1, v2: V2 }[];
  filterOneToMany(d1Key: keyof V1, iterator?: (v1: V1, v2: V2) => boolean): { v1: V1, v2: V2 }[];
  everyManyToManyJunction<J extends any>(junction: (J[] | Record<string, J>), d1Key: keyof J, d2Key: keyof J, exist: (v1: V1, v2: V2) => void): this;
  includeModel<M>(model: M): this;
  readonly model: any;
}

type mapIterator<V1, V2> = (v1: V1, v2: V2) => V2 | undefined;
type postIterator<V1> = (fromKey: string, toKey: string) => void;

export class Relationship<V1 extends IIndexable, V2 extends IIndexable> implements IRelationship<V1, V2> {

  private __model: any;

  constructor(
    private __d1: IDatasource<V1>,
    private __d2: IDatasource<V2>) {

  }

  get d1() {
    return this.__d1;
  }

  get d2() {
    return this.__d2;
  }

  includeModel<M>(model: M): this {
    this.__model = _4.merge({}, this.__model, model);
    return this;
  }

  get model(): any {
    return this.__model;
  }

  /**
   * erase any key which does not exist
   * @pre d1 has a property with an array of id's. d2 has the id reference
   * @post d1 ...
   * @param d1Key array of id keys mapped to d2
   */
  packManyToMany(d1Key: keyof V1): this {
    this.__d1.each((element) => {
      if (element[d1Key]) {
        element[`_${d1Key}`] = _4.filter(element[d1Key]  as any, (id: string) => {
          return this.__d2.exists(id);
        });
      }
    });

    return this;
  }

  packOneToMany(d1Key: keyof V1): this {
    this.__d1.each((element) => {
      if (element[d1Key]) {
        if (!this.__d2.exists(element[d1Key]  as any)) {
          element[`_${d1Key}`] = undefined;
        }
      }
    });

    return this;
  }

  /**
   * @pre d1 has a property with an array of id's. d2 has the id reference
   * @post d1 has a new property with an underscore prefix of the objects, that exist
   * @param d1Key array of id keys mapped to d2
   */
  mapManyToMany(d1Key: keyof V1, iterator: mapIterator<V1, V2> = (_0, v2) => v2): this {
    this.__d1.each((element) => {
      if (element[d1Key]) {
        const v1 = element[d1Key] as any;

        element[`_${d1Key}`] = _4.compact(_4.map(_4.compact(_4.map(v1, (id: string) => {
          return this.__d2.get(id);
        })), (v2: V2) => {
          return iterator(element, v2);
        }));
      }
    });

    return this;
  }

  /**
   *
   * @pre d1 has a property with an array of id's. d2 has the id reference
   * @param d1Key array of id keys mapped to d2
   */
  mapOneToMany(d1Key: keyof V1, iterator: mapIterator<V1, V2> = (_0, v2) => v2): this {
    this.__d1.each((element) => {
      const v1 = element[d1Key] as any;
      const v2 = this.__d2.get(v1);

      if (v1 && v2) {
        element[`_${d1Key}`] = iterator(element, v2);
      }

    });

    return this;
  }

  /**
   * call `iterator ` for every d1 key which maps to a d2 key that exists
   * @pre d1 has a property with an array of id's. d2 has the id reference
   * @param d1Key array of id keys mapped to d2
   * @param iterator
   */
  everyManyToMany(d1Key: keyof V1, iterator: (v1: V1, v2: V2) => void): this {
    this.__d1.each((v1) => {
      _4.each(v1[d1Key] as any, (id: string) => {
        const v2 = this.__d2.get(id);

        if (v1 && v2) {
          iterator(v1, v2);
        }
      });
    });

    return this;
  }

  /**
   * call `iterator ` for every d1 key which maps to a d2 key that exists
   * @pre d1 has a property with an array of id's. d2 has the id reference
   * @param d1Key array of id keys mapped to d2
   * @param iterator chooses which elements are passed
   */
  filterManyToMany(d1Key: keyof V1, iterator: (v1: V1, v2: V2) => boolean = (_0, _1) => true): { v1: V1, v2: V2 }[] {
    const filteredList: { v1: V1, v2: V2 }[] = [];

    this.__d1.each((v1) => {
      _4.each(v1[d1Key] as any, (id: string) => {
        const v2 = this.__d2.get(id);

        if (v1 && v2 && iterator(v1, v2)) {
          filteredList.push({
            v1, v2
          });
        }
      });
    });

    return filteredList;
  }

  /**
   *
   * @pre d1 has a property with an array of id's. d2 has the id reference
   * @param d1Key array of id keys mapped to d2
   */
  everyOneToMany = this.everyManyToMany; // the lodash each supports one or many

  /**
   *
   * @pre d1 has a property with an array of id's. d2 has the id reference
   * @param d1Key array of id keys mapped to d2
   */
  filterOneToMany = this.filterManyToMany; // the lodash each supports one or many

  /**
   *
   * @param junction an array of objects with d1 and d2 keys
   * @param d1Key the key in the ruleset which holds an id for d1
   * @param d2Key the key in the ruleset which holds an id for d2
   * @param iterator if both id's exist, then invoke
   */
  everyManyToManyJunction<J extends any>(junction: (J[] | Record<string, J>), d1Key: keyof J, d2Key: keyof J, iterator: (v1: V1, v2: V2) => void): this {
    _4.each(junction, (rule) => {
      const v1 = this.__d1.get(rule[d1Key]);
      const v2 = this.__d2.get(rule[d2Key]);

      if (v1 && v2) {
        iterator(v1, v2);
      }
    });

    return this;
  }

}




// WEBPACK FOOTER //
// ./app/vendor/utility-ts-orm/dist/types/relationship.ts